import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Package, Plane, Search, CheckCircle, CheckCircle2, MapPin,
  Clock, Shield, Truck, PackageCheck, ClipboardCheck, RefreshCw,
  User, Phone, Mail, MessageSquare, Ship,
} from 'lucide-react';
import { STATUS_LABELS, STATUS_COLORS } from '../lib/constants';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import PublicPageShell from '../components/landing/PublicPageShell';
import PackageStatusBadge from '../components/PackageStatusBadge';
import { CountryRoute } from '../components/CountryDisplay';
import { getCountryWithFlag } from '../lib/countries';
import '../styles/professional-theme.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Milestone mapping: 12 statuses → 5 progress milestones ───
const MILESTONES = {
  fr: [
    { key: 'created', label: 'Créée', statuses: ['registered', 'pending_collection'] },
    { key: 'embarked', label: 'Embarquée', statuses: ['collected', 'verified'] },
    { key: 'transit', label: 'En transit', statuses: ['in_transit'] },
    { key: 'customs', label: 'Dédouanée', statuses: ['arrived', 'awaiting_customs', 'customs_cleared'] },
    { key: 'delivered', label: 'Livrée', statuses: ['awaiting_delivery', 'out_for_delivery', 'ready_for_pickup', 'delivered'] },
  ],
  en: [
    { key: 'created', label: 'Created', statuses: ['registered', 'pending_collection'] },
    { key: 'embarked', label: 'Shipped', statuses: ['collected', 'verified'] },
    { key: 'transit', label: 'In Transit', statuses: ['in_transit'] },
    { key: 'customs', label: 'Cleared', statuses: ['arrived', 'awaiting_customs', 'customs_cleared'] },
    { key: 'delivered', label: 'Delivered', statuses: ['awaiting_delivery', 'out_for_delivery', 'ready_for_pickup', 'delivered'] },
  ],
};

// ─── Event icon + color mapping for detailed history ───
const EVENT_CONFIG = {
  registered:         { icon: Package,       color: '#9ca3af', bg: '#f3f4f6' },
  pending_collection: { icon: Clock,         color: '#9ca3af', bg: '#f3f4f6' },
  collected:          { icon: PackageCheck,  color: '#3b82f6', bg: '#eff6ff' },
  verified:           { icon: ClipboardCheck,color: '#3b82f6', bg: '#eff6ff' },
  in_transit:         { icon: Plane,         color: '#3b82f6', bg: '#eff6ff' },
  arrived:            { icon: MapPin,        color: '#f97316', bg: '#fff7ed' },
  awaiting_customs:   { icon: Clock,         color: '#f97316', bg: '#fff7ed' },
  customs_cleared:    { icon: Shield,        color: '#16a34a', bg: '#f0fdf4' },
  awaiting_delivery:  { icon: PackageCheck,  color: '#f97316', bg: '#fff7ed' },
  out_for_delivery:   { icon: Truck,         color: '#3b82f6', bg: '#eff6ff' },
  ready_for_pickup:   { icon: PackageCheck,  color: '#16a34a', bg: '#f0fdf4' },
  delivered:          { icon: CheckCircle2,  color: '#16a34a', bg: '#f0fdf4' },
};

// ─── Detailed event descriptions ───
const EVENT_DESCRIPTIONS = {
  fr: {
    registered: 'Colis enregistré dans le système',
    pending_collection: 'En attente de collecte par le partenaire',
    collected: 'Colis collecté et pris en charge',
    verified: 'Colis vérifié et conforme',
    in_transit: 'Colis en cours de transport',
    arrived: 'Colis arrivé à destination',
    awaiting_customs: 'En cours de dédouanement',
    customs_cleared: 'Dédouanement validé',
    awaiting_delivery: 'En attente de livraison locale',
    out_for_delivery: 'En cours de livraison locale',
    ready_for_pickup: 'Colis livré en point relais',
    delivered: 'Colis livré au destinataire',
  },
  en: {
    registered: 'Package registered in system',
    pending_collection: 'Awaiting collection by partner',
    collected: 'Package collected and secured',
    verified: 'Package verified and compliant',
    in_transit: 'Package in transit',
    arrived: 'Package arrived at destination',
    awaiting_customs: 'Customs clearance in progress',
    customs_cleared: 'Customs clearance validated',
    awaiting_delivery: 'Awaiting local delivery',
    out_for_delivery: 'Out for local delivery',
    ready_for_pickup: 'Package delivered to relay point',
    delivered: 'Package delivered to recipient',
  },
};

// ─── Horizontal Progress Bar ───
const ProgressionBar = ({ packageData, language }) => {
  const { t } = useLanguage();
  const milestones = MILESTONES[language] || MILESTONES.fr;
  const currentStatus = packageData.status;
  const history = packageData.status_history || [];

  // Find which milestone is current
  let currentMilestoneIdx = -1;
  milestones.forEach((m, i) => {
    if (m.statuses.includes(currentStatus)) currentMilestoneIdx = i;
  });

  // Find which milestones are completed (have at least one status in history)
  const milestoneTimestamps = milestones.map((m) => {
    const entries = history.filter((h) => m.statuses.includes(h.status));
    if (entries.length === 0) return null;
    return entries[entries.length - 1].timestamp;
  });

  const isDelivered = currentStatus === 'delivered';
  const eta = packageData.shipment_info?.expected_arrival_date;

  return (
    <Card className="analytics-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm font-semibold text-foreground">
            {language === 'en' ? 'Shipment Progress' : 'Progression de l\'expédition'}
          </span>
          {eta && (
            <span className="text-xs text-muted-foreground">
              ETA : {new Date(eta).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short' })}
              {isDelivered && (
                <span className="ml-2 text-green-600 font-medium">
                  · {language === 'en' ? 'Delivered on time' : 'Livré à l\'heure'}
                </span>
              )}
            </span>
          )}
        </div>

        <div className="relative">
          {/* Background line */}
          <div className="absolute top-[14px] left-0 right-0 h-[3px] bg-gray-200 rounded-full" />
          {/* Progress line */}
          {currentMilestoneIdx >= 0 && (
            <div
              className="absolute top-[14px] left-0 h-[3px] rounded-full transition-all duration-700"
              style={{
                width: `${isDelivered ? 100 : (currentMilestoneIdx / (milestones.length - 1)) * 100}%`,
                background: '#e8590c',
              }}
            />
          )}

          {/* Milestone dots + labels */}
          <div className="relative flex justify-between">
            {milestones.map((m, i) => {
              const isCompleted = i < currentMilestoneIdx || (i === currentMilestoneIdx && isDelivered);
              const isCurrent = i === currentMilestoneIdx && !isDelivered;
              const ts = milestoneTimestamps[i];

              return (
                <div key={m.key} className="flex flex-col items-center" style={{ width: `${100 / milestones.length}%` }}>
                  <div
                    className={`w-7 h-7 rounded-full border-[3px] flex items-center justify-center z-10 transition-all ${
                      isCompleted
                        ? 'bg-[#e8590c] border-[#e8590c]'
                        : isCurrent
                        ? 'bg-white border-[#e8590c] ring-4 ring-[#e8590c]/20'
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    {isCompleted && <CheckCircle size={14} className="text-white" />}
                    {isCurrent && <div className="w-2 h-2 rounded-full bg-[#e8590c]" />}
                  </div>
                  <span className={`text-xs font-medium mt-2 text-center ${
                    isCompleted || isCurrent ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {m.label}
                  </span>
                  {ts && (
                    <span className="text-[10px] text-muted-foreground mt-0.5">
                      {new Date(ts).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: 'short' })}{' '}
                      {new Date(ts).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Detailed History Timeline ───
const DetailedHistory = ({ packageData, language }) => {
  const history = [...(packageData.status_history || [])].reverse();
  const descriptions = EVENT_DESCRIPTIONS[language] || EVENT_DESCRIPTIONS.fr;
  const labels = STATUS_LABELS[language] || STATUS_LABELS.fr;

  return (
    <Card className="analytics-card">
      <CardHeader className="analytics-card-header">
        <div className="flex items-center justify-between w-full">
          <div>
            <CardTitle className="analytics-card-title">
              {language === 'en' ? 'Detailed History' : 'Historique détaillé'}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {language === 'en' ? 'All package events' : 'Tous les événements du colis'}
            </p>
          </div>
          <Button variant="ghost" size="sm" className="gap-1.5 text-xs text-muted-foreground">
            <RefreshCw size={12} />
            {language === 'en' ? 'Refresh' : 'Actualiser'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-0">
          {history.map((event, idx) => {
            const config = EVENT_CONFIG[event.status] || EVENT_CONFIG.registered;
            const Icon = config.icon;
            const isFirst = idx === 0;
            const isLast = idx === history.length - 1;
            const ts = event.timestamp ? new Date(event.timestamp) : null;

            return (
              <div key={idx} className="relative flex gap-4 pb-6 last:pb-0">
                {/* Vertical connector line */}
                {!isLast && (
                  <div
                    className="absolute left-[17px] top-[38px] w-[2px] bottom-0"
                    style={{ background: '#e5e7eb' }}
                  />
                )}

                {/* Colored icon */}
                <div
                  className="w-[36px] h-[36px] rounded-full shrink-0 flex items-center justify-center z-10"
                  style={{ background: config.bg, color: config.color }}
                >
                  <Icon size={16} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-foreground">
                      {descriptions[event.status] || labels[event.status] || event.status}
                    </span>
                    {isFirst && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        {language === 'en' ? 'Latest' : 'Dernier'}
                      </span>
                    )}
                  </div>
                  {event.note && (
                    <p className="text-xs text-muted-foreground mt-0.5">{event.note}</p>
                  )}
                  {ts && (
                    <p className="text-xs text-muted-foreground mt-1">
                      <span className="font-medium">{ts.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: 'short' })}</span>
                      {' · '}
                      {ts.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {history.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              {language === 'en' ? 'No events yet' : 'Aucun événement'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Transport / Route Card ───
const TransportCard = ({ packageData, language }) => {
  const info = packageData.shipment_info;
  if (!info) return null;

  const origin = packageData.origin_country || '';
  const dest = packageData.destination_country || '';
  const flightName = info.flight_name || '';
  const transportMode = packageData.transport_mode || 'aerien';
  const TransportIcon = transportMode === 'maritime' ? Ship : Plane;

  return (
    <Card className="analytics-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <TransportIcon size={16} className="text-muted-foreground" />
          <span className="font-semibold text-sm">{language === 'en' ? 'Route' : 'Trajet'}</span>
          {flightName && (
            <span className="text-xs text-muted-foreground">
              {transportMode === 'maritime' ? '' : (language === 'en' ? 'Flight' : 'Vol')} {flightName}
            </span>
          )}
        </div>

        {/* Route visualization */}
        <div className="relative bg-muted/50 rounded-xl p-6 mb-4">
          <div className="flex items-center justify-between">
            {/* Origin */}
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#e8590c] text-white grid place-items-center mx-auto mb-1">
                <TransportIcon size={16} />
              </div>
              <div className="text-xs font-bold">{getCountryWithFlag(origin).flag} {getCountryWithFlag(origin).code}</div>
              <div className="text-[10px] text-muted-foreground">{getCountryWithFlag(origin).name}</div>
            </div>

            {/* Route line */}
            <div className="flex-1 mx-4 relative">
              <div className="h-[2px] bg-gray-300 w-full" />
              <div
                className="absolute top-[-3px] h-[8px] bg-[#e8590c] rounded-full"
                style={{ width: packageData.status === 'delivered' ? '100%' : '60%' }}
              />
              <div className="absolute top-[-8px] right-[40%]">
                <TransportIcon size={14} className="text-[#e8590c]" />
              </div>
            </div>

            {/* Destination */}
            <div className="text-center">
              <div className={`w-10 h-10 rounded-full grid place-items-center mx-auto mb-1 ${
                packageData.status === 'delivered' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                <MapPin size={16} />
              </div>
              <div className="text-xs font-bold">{getCountryWithFlag(dest).flag} {getCountryWithFlag(dest).code}</div>
              <div className="text-[10px] text-muted-foreground">{getCountryWithFlag(dest).name}</div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-[10px] uppercase text-muted-foreground tracking-wider">
              {language === 'en' ? 'Weight' : 'Poids'}
            </div>
            <div className="text-sm font-bold">{packageData.weight || '—'} kg</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground tracking-wider">
              {language === 'en' ? 'Type' : 'Type'}
            </div>
            <div className="text-sm font-bold capitalize">{transportMode === 'maritime' ? 'Maritime' : (language === 'en' ? 'Air' : 'Aérien')}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground tracking-wider">
              {language === 'en' ? 'Departure' : 'Départ'}
            </div>
            <div className="text-sm font-bold">
              {info.departure_date
                ? new Date(info.departure_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: 'short' })
                : '—'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Parties Prenantes Card ───
const StakeholdersCard = ({ packageData, language }) => {
  const Avatar = ({ name, color = '#1a1a1a' }) => {
    const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    return (
      <div
        className="w-9 h-9 rounded-full text-white text-xs font-bold grid place-items-center shrink-0"
        style={{ background: color }}
      >
        {initials}
      </div>
    );
  };

  return (
    <Card className="analytics-card">
      <CardContent className="p-5">
        <h3 className="font-semibold text-sm mb-4">
          {language === 'en' ? 'Stakeholders' : 'Parties prenantes'}
        </h3>

        <div className="space-y-4">
          {/* Sender */}
          <div className="flex items-start gap-3">
            <Avatar name={packageData.sender_name} color="#e8590c" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                {language === 'en' ? 'Sender' : 'Expéditeur'}
              </div>
              <div className="text-sm font-semibold truncate">{packageData.sender_name || '—'}</div>
              {packageData.sender_phone && (
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Phone size={10} />
                  {packageData.sender_phone}
                </div>
              )}
              {packageData.sender_email && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail size={10} />
                  {packageData.sender_email}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-border" />

          {/* Recipient */}
          <div className="flex items-start gap-3">
            <Avatar name={packageData.recipient_name} color="#1a1a1a" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                {language === 'en' ? 'Recipient' : 'Destinataire'}
              </div>
              <div className="text-sm font-semibold truncate">{packageData.recipient_name || '—'}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {[packageData.destination_city, packageData.destination_country].filter(Boolean).join(', ') || '—'}
              </div>
              {packageData.recipient_phone && (
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Phone size={10} />
                  {packageData.recipient_phone}
                </div>
              )}
            </div>
          </div>

          {/* Partner / Agent */}
          {packageData.partner_name && (
            <>
              <div className="border-t border-border" />
              <div className="flex items-start gap-3">
                <Avatar name={packageData.partner_name} color="#666" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                    {language === 'en' ? 'Collection Agent' : 'Agent en charge'}
                  </div>
                  <div className="text-sm font-semibold truncate">{packageData.partner_name}</div>
                </div>
              </div>
            </>
          )}

          {/* Relay Point */}
          {packageData.delivery_mode === 'relay_point' && packageData.relay_point_info && (
            <>
              <div className="border-t border-border" />
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-full text-white text-xs font-bold grid place-items-center shrink-0"
                  style={{ background: '#2563eb' }}
                >
                  <MapPin size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                    {language === 'en' ? 'Relay Point' : 'Point Relais'}
                  </div>
                  <div className="text-sm font-semibold truncate">{packageData.relay_point_info.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {packageData.relay_point_info.address}, {packageData.relay_point_info.city}
                  </div>
                  {packageData.relay_point_info.phone && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Phone size={10} />
                      {packageData.relay_point_info.phone}
                    </div>
                  )}
                  {packageData.relay_point_info.opening_hours && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock size={10} />
                      {packageData.relay_point_info.opening_hours}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main Component ───
const TrackPackage = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [oceCode, setOceCode] = useState('');
  const [packageData, setPackageData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const trackPackage = useCallback(async (code) => {
    if (!code) return;
    setError('');
    setPackageData(null);
    setLoading(true);
    try {
      const response = await axios.get(`${API}/packages/track/${code}`);
      setPackageData(response.data);
    } catch (err) {
      setError(t('packageNotFound'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      const normalized = code.toUpperCase().trim();
      setOceCode(normalized);
      trackPackage(normalized);
    }
  }, [searchParams, trackPackage]);

  const handleTrack = async (e) => {
    e.preventDefault();
    trackPackage(oceCode);
  };

  const statusLabels = STATUS_LABELS[language] || STATUS_LABELS.fr;
  const transportMode = packageData?.transport_mode || 'aerien';

  return (
    <PublicPageShell
      eyebrow={t('footerSupport')}
      title={t('trackAPackage')}
      lede={language === 'en'
        ? 'Enter the OCE code from your confirmation to follow your shipment, step by step.'
        : 'Saisissez le code OCE de votre confirmation pour suivre votre envoi, étape par étape.'}
    >
      <SEOHead
        title={t('seoTrackTitle')}
        description={t('seoTrackDesc')}
        canonical="/track"
      />
      <div>
        {/* Recherche */}
        <Card className="analytics-card mb-6">
          <CardContent className="p-6">
            <form onSubmit={handleTrack} className="flex gap-2 max-w-lg mx-auto">
              <Input
                type="text"
                placeholder={language === 'en' ? 'OCE code (e.g. OCE12345678)' : 'Code OCE (ex: OCE12345678)'}
                value={oceCode}
                onChange={(e) => setOceCode(e.target.value.toUpperCase())}
                required
                className="text-base"
              />
              <Button type="submit" disabled={loading} size="lg" className="gap-1.5">
                <Search size={16} />
                {loading ? '...' : (language === 'en' ? 'Track' : 'Suivre')}
              </Button>
            </form>
            {error && (
              <Alert variant="destructive" className="mt-3 max-w-lg mx-auto">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {packageData && (
          <div className="space-y-5">
            {/* Package Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-bold">{packageData.oce_code}</h2>
                  <PackageStatusBadge status={packageData.status} />
                  <Badge variant="outline" className="text-xs gap-1">
                    {transportMode === 'maritime' ? <Ship size={10} /> : <Plane size={10} />}
                    {transportMode === 'maritime' ? 'Maritime' : (language === 'en' ? 'Air' : 'Aérien')}
                  </Badge>
                  {packageData.freight_type && (
                    <Badge variant="outline" className="text-xs capitalize">
                      {packageData.freight_type}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1 flex-wrap">
                  <CountryRoute origin={packageData.origin_country} destination={packageData.destination_country} size="sm" />
                  {packageData.sender_name && <span>· {packageData.sender_name}</span>}
                  {packageData.created_at && (
                    <span>· {language === 'en' ? 'created' : 'créé le'} {new Date(packageData.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short' })}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Horizontal Progress Bar */}
            <ProgressionBar packageData={packageData} language={language} />

            {/* Two-column layout */}
            <div className="grid lg:grid-cols-5 gap-5">
              {/* Left: Detailed History (3 cols) */}
              <div className="lg:col-span-3">
                <DetailedHistory packageData={packageData} language={language} />
              </div>

              {/* Right: Transport + Stakeholders (2 cols) */}
              <div className="lg:col-span-2 space-y-5">
                <TransportCard packageData={packageData} language={language} />
                <StakeholdersCard packageData={packageData} language={language} />
              </div>
            </div>
          </div>
        )}
      </div>
    </PublicPageShell>
  );
};

export default TrackPackage;
